import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copyright, Shield, FileText, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

export default function CopyrightPolicy() {
    const navigate = useNavigate();
    const { t } = useLanguage();

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
                <div className="container mx-auto px-4 py-4 flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex items-center gap-2">
                        <Copyright className="w-5 h-5 text-primary" />
                        <h1 className="text-xl font-semibold">Copyright Policy</h1>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="container mx-auto px-4 py-8 max-w-4xl">
                <div className="prose prose-slate dark:prose-invert max-w-none">
                    
                    <p className="text-muted-foreground text-lg">
                        Last Updated: June 7, 2025
                    </p>

                    <section className="mt-8">
                        <h2 className="flex items-center gap-2 text-2xl font-semibold">
                            <Shield className="w-6 h-6 text-primary" />
                            1. Copyright Ownership
                        </h2>
                        <p>
                            All content, features, and functionality of the FendanaDoc MediGuide application and website 
                            (collectively, the "Service"), including but not limited to text, graphics, logos, icons, 
                            images, audio clips, digital downloads, data compilations, software, and the compilation 
                            thereof, are the exclusive property of FendanaDoc Technologies and its licensors and are 
                            protected by international copyright, trademark, patent, trade secret, and other intellectual 
                            property or proprietary rights laws.
                        </p>
                    </section>

                    <section className="mt-8">
                        <h2 className="flex items-center gap-2 text-2xl font-semibold">
                            <FileText className="w-6 h-6 text-primary" />
                            2. Permitted Use
                        </h2>
                        <p>You are granted a limited, non-exclusive, non-transferable license to:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Access and use the Service for personal, non-commercial purposes</li>
                            <li>Download and print content for personal reference only</li>
                            <li>Share links to the Service with proper attribution</li>
                            <li>Use the health information provided for your personal health management</li>
                        </ul>
                    </section>

                    <section className="mt-8">
                        <h2 className="text-2xl font-semibold">3. Prohibited Use</h2>
                        <p>You may NOT:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Copy, reproduce, distribute, or create derivative works from our content without written permission</li>
                            <li>Use any data mining, robots, or similar data gathering tools on our Service</li>
                            <li>Remove any copyright, trademark, or proprietary notices from our content</li>
                            <li>Use our trademarks, logos, or brand elements without authorization</li>
                            <li>Reverse engineer, decompile, or disassemble any software components</li>
                            <li>Use the Service or its content for any commercial purpose without a license</li>
                            <li>Frame or mirror any part of the Service without written consent</li>
                            <li>Republish our medical content on other platforms without attribution and permission</li>
                        </ul>
                    </section>

                    <section className="mt-8">
                        <h2 className="text-2xl font-semibold">4. Trademarks</h2>
                        <p>
                            The following are trademarks of FendanaDoc Technologies:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>"FendanaDoc" and the FendanaDoc logo</li>
                            <li>"MediGuide" and the MediGuide logo</li>
                            <li>"FendanaDoc MediGuide" combined mark</li>
                            <li>All associated taglines and slogans</li>
                        </ul>
                        <p className="mt-4">
                            These trademarks may not be used in connection with any product or service that is not 
                            ours, in any manner that is likely to cause confusion, or in any manner that disparages 
                            or discredits FendanaDoc Technologies.
                        </p>
                    </section>

                    <section className="mt-8">
                        <h2 className="text-2xl font-semibold">5. User-Generated Content</h2>
                        <p>
                            When you submit content to our Service (such as symptom descriptions, health diary entries, 
                            or feedback):
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>You retain ownership of your original content</li>
                            <li>You grant us a non-exclusive, royalty-free license to use, process, and store your content to provide the Service</li>
                            <li>You represent that you have the right to submit such content</li>
                            <li>You agree not to submit content that infringes on others' intellectual property rights</li>
                        </ul>
                    </section>

                    <section className="mt-8">
                        <h2 className="text-2xl font-semibold">6. Medical Content Disclaimer</h2>
                        <p>
                            The medical and health information provided through our Service is for informational 
                            purposes only. While we strive for accuracy:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Our AI-generated assessments are not a substitute for professional medical advice</li>
                            <li>Health library content is compiled from various medical sources and is protected by copyright</li>
                            <li>Reproduction of medical content for commercial purposes is strictly prohibited</li>
                            <li>Always consult qualified healthcare providers for medical decisions</li>
                        </ul>
                    </section>

                    <section className="mt-8">
                        <h2 className="text-2xl font-semibold">7. Third-Party Content</h2>
                        <p>
                            Our Service may include content from third-party sources, including:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Medical information databases</li>
                            <li>Map and location services (Google Maps)</li>
                            <li>Payment processors (Stripe, PayPal)</li>
                            <li>AI language models for symptom analysis</li>
                        </ul>
                        <p className="mt-4">
                            Such third-party content remains the property of its respective owners and is subject 
                            to their terms and copyright policies.
                        </p>
                    </section>

                    <section className="mt-8">
                        <h2 className="text-2xl font-semibold">8. DMCA Notice</h2>
                        <p>
                            If you believe that any content on our Service infringes your copyright, please send a 
                            Digital Millennium Copyright Act (DMCA) notice to our designated agent with the following 
                            information:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Your physical or electronic signature</li>
                            <li>Identification of the copyrighted work claimed to be infringed</li>
                            <li>Identification of the infringing material and its location on our Service</li>
                            <li>Your contact information (address, phone number, email)</li>
                            <li>A statement that you have a good faith belief that the use is not authorized</li>
                            <li>A statement, under penalty of perjury, that the information is accurate and you are authorized to act on behalf of the copyright owner</li>
                        </ul>
                    </section>

                    <section className="mt-8">
                        <h2 className="text-2xl font-semibold">9. Counter-Notification</h2>
                        <p>
                            If you believe your content was wrongly removed due to a DMCA notice, you may submit a 
                            counter-notification containing:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Your physical or electronic signature</li>
                            <li>Identification of the removed content and its previous location</li>
                            <li>A statement under penalty of perjury that you have a good faith belief the content was removed by mistake</li>
                            <li>Your name, address, and phone number</li>
                            <li>Consent to jurisdiction of federal court in your district</li>
                        </ul>
                    </section>

                    <section className="mt-8">
                        <h2 className="text-2xl font-semibold">10. Enforcement</h2>
                        <p>
                            We take copyright infringement seriously. Violations may result in:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Immediate termination of your account</li>
                            <li>Removal of infringing content</li>
                            <li>Legal action seeking damages and injunctive relief</li>
                            <li>Reporting to relevant authorities</li>
                        </ul>
                    </section>

                    <section className="mt-8">
                        <h2 className="flex items-center gap-2 text-2xl font-semibold">
                            <Mail className="w-6 h-6 text-primary" />
                            11. Contact Us
                        </h2>
                        <p>
                            For copyright inquiries, permissions, or to report infringement:
                        </p>
                        <div className="bg-muted p-4 rounded-lg mt-4">
                            <p><strong>FendanaDoc Technologies</strong></p>
                            <p>Copyright Agent</p>
                            <p>Email: copyright@mediguide.online</p>
                            <p>Website: https://mediguide.online</p>
                        </div>
                    </section>

                    <section className="mt-8 p-4 bg-primary/5 rounded-lg border border-primary/20">
                        <p className="text-sm text-muted-foreground">
                            © 2025 FendanaDoc Technologies. All rights reserved. This Copyright Policy is subject 
                            to change without notice. Please check back periodically for updates.
                        </p>
                    </section>

                </div>
            </main>
        </div>
    );
}
